import {
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";

import { auth, db } from "../firebase";

const ADMIN_UID = "imXv7XiyoYSxJRg9kLzo7jhrbJI3";
const MANAGER_UID = "qrvQ8zvbtobSxHPkVGgx2c63t9B2";

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeRoom = (value) =>
  clean(value).toUpperCase();

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const removeUndefined = (object = {}) =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined)
  );

/*
 * PHASE 3B SECURITY:
 * Audit actor browser localStorage se nahi liya jata.
 * Firebase Auth currentUser hi identity source-of-truth hai.
 */
export const getCurrentActor = () => {
  const user = auth.currentUser;

  if (!user) {
    return {
      actorId: "",
      actorName: "Unauthenticated",
      actorEmail: "",
      actorRole: "unauthenticated",
    };
  }

  const actorRole =
    user.uid === ADMIN_UID
      ? "admin"
      : user.uid === MANAGER_UID
      ? "manager"
      : "user";

  const actorName =
    actorRole === "admin"
      ? "Admin"
      : actorRole === "manager"
      ? "Manager"
      : clean(user.displayName) || clean(user.email) || "User";

  return {
    actorId: clean(user.uid),
    actorName,
    actorEmail: clean(user.email),
    actorRole,
  };
};

export const createAuditLog = async ({
  hotelId,
  hotelName = "",
  action,
  title,
  description = "",
  room = "",
  guestName = "",
  phoneNumber = "",
  stayId = "",
  guestProfileId = "",
  invoiceNumber = "",
  receiptNumber = "",
  referenceId = "",
  amount,
  before,
  after,
  changes,
  metadata,
  eventAt = new Date(),
}) => {
  if (!clean(hotelId)) {
    console.warn("Audit log skipped: hotelId missing.");
    return null;
  }

  if (!clean(action)) {
    console.warn("Audit log skipped: action missing.");
    return null;
  }

  const currentActor = getCurrentActor();

  if (!currentActor.actorId) {
    console.warn("Audit log skipped: authenticated actor missing.");
    return null;
  }

  const eventDate =
    eventAt instanceof Date ? eventAt : new Date(eventAt);

  const validEventDate = Number.isNaN(eventDate.getTime())
    ? new Date()
    : eventDate;

  const payload = removeUndefined({
    hotelId: clean(hotelId),
    hotelName: clean(hotelName),
    action: clean(action),
    actionType: clean(action),
    title: clean(title) || clean(action),
    description: clean(description),
    room: normalizeRoom(room),
    guestName: clean(guestName),
    phoneNumber: clean(phoneNumber),
    stayId: clean(stayId),
    guestProfileId: clean(guestProfileId),
    invoiceNumber: clean(invoiceNumber),
    receiptNumber: clean(receiptNumber),
    referenceId: clean(
      referenceId || stayId || invoiceNumber || receiptNumber
    ),
    amount:
      amount === undefined || amount === null
        ? undefined
        : safeNumber(amount),
    before: before || undefined,
    after: after || undefined,
    changes: changes || undefined,
    metadata: metadata || undefined,

    // Caller-supplied identity intentionally ignored.
    actorId: currentActor.actorId,
    actorName: currentActor.actorName,
    actorEmail: currentActor.actorEmail,
    actorRole: currentActor.actorRole,

    eventAt: validEventDate.toISOString(),
    createdAt: serverTimestamp(),
  });

  try {
    const documentReference = await addDoc(
      collection(db, "auditLogs"),
      payload
    );

    return {
      id: documentReference.id,
      ...payload,
    };
  } catch (error) {
    console.error("Audit log create error:", error);
    return null;
  }
};

export const logGuestCheckIn = (data) =>
  createAuditLog({
    ...data,
    action: "check_in",
    title:
      data.title ||
      `Guest checked in${
        data.room ? ` - Room ${normalizeRoom(data.room)}` : ""
      }`,
  });

export const logGuestCheckout = (data) =>
  createAuditLog({
    ...data,
    action: "checkout",
    title:
      data.title ||
      `Guest checked out${
        data.room ? ` - Room ${normalizeRoom(data.room)}` : ""
      }`,
  });

export const logPaymentReceived = (data) =>
  createAuditLog({
    ...data,
    action: "payment",
    title: data.title || "Payment Received",
  });

export const logExpenseCreated = (data) =>
  createAuditLog({
    ...data,
    action: "expense",
    title: data.title || "Hotel Expense Added",
  });

export const logGuestProfileUpdate = (data) =>
  createAuditLog({
    ...data,
    action: "guest_profile",
    title: data.title || "Guest Profile Updated",
  });

export const logHousekeepingUpdate = (data) =>
  createAuditLog({
    ...data,
    action: "housekeeping",
    title:
      data.title ||
      `Housekeeping updated${
        data.room ? ` - Room ${normalizeRoom(data.room)}` : ""
      }`,
  });

export const logMaintenanceUpdate = (data) =>
  createAuditLog({
    ...data,
    action: "maintenance",
    title:
      data.title ||
      `Maintenance updated${
        data.room ? ` - Room ${normalizeRoom(data.room)}` : ""
      }`,
  });

export const logRoomBoardUpdate = (data) =>
  createAuditLog({
    ...data,
    action: "room_board",
    title: data.title || "Room Board Updated",
  });

export const logDiscountUpdate = (data) =>
  createAuditLog({
    ...data,
    action: "discount",
    title: data.title || "Discount / GST Updated",
  });

const auditLogService = {
  createAuditLog,
  logGuestCheckIn,
  logGuestCheckout,
  logPaymentReceived,
  logExpenseCreated,
  logGuestProfileUpdate,
  logHousekeepingUpdate,
  logMaintenanceUpdate,
  logRoomBoardUpdate,
  logDiscountUpdate,
};

export default auditLogService;
